import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useBranding, assetUrl, brandTextColor } from "@/lib/branding";
import { TwoFactorChallenge } from "@/components/auth/TwoFactorChallenge";
import { RecoveryFlow } from "@/components/auth/RecoveryFlow";
import { useForceDark } from "@/context/ThemeContext";

export default function Login() {
  useForceDark();
  const { login } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const brandName = branding?.brand?.name || "Tracify";
  const brandLogo = assetUrl(branding?.brand?.logo_id);
  const accent = branding?.brand?.accent_color || "#FFFFFF";
  const accentText = brandTextColor(accent);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password, remember);
    setLoading(false);
    if (res.ok) navigate("/");
    else if (res.twofa) setChallenge(res.challenge);
    else toast.error(res.error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.04),transparent_45%)]" />
      <div className="relative grid w-full max-w-5xl overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0F0F11] lg:grid-cols-2">
        {/* Brand panel */}
        <div className="hidden flex-col justify-between border-r border-white/[0.08] p-10 lg:flex">
          <div className="flex items-center gap-2.5">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="h-9 w-auto object-contain" data-testid="login-logo" />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: accent, color: accentText }}>
                <span className="font-display text-lg font-black">{brandName.charAt(0)}</span>
              </div>
            )}
            <span className="font-display text-lg font-bold tracking-tight" data-testid="login-brand">{brandName}</span>
          </div>
          <div>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight">
              {branding?.landing?.headline || "The affiliate platform built for scale."}
            </h2>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground">
              {branding?.landing?.subheadline || "Multi-tenant SaaS for networks, managers and media buyers. Offers, streams, tracking and payouts — in one clean control room."}
            </p>
          </div>
          <div className="flex gap-6 text-xs text-muted-foreground">
            <span>RBAC</span><span>Multi-tenant</span><span>Crypto payouts</span>
          </div>
        </div>

        {/* Form */}
        <div className="p-8 lg:p-10">
          {forgot ? (
            <RecoveryFlow accent="#FFFFFF" onBack={() => setForgot(false)} />
          ) : challenge ? (
            <TwoFactorChallenge
              challenge={challenge}
              accent={accent}
              accentText={accentText}
              remember={remember}
              onSuccess={() => navigate("/")}
              onCancel={() => { setChallenge(null); setPassword(""); }}
            />
          ) : (
          <>
          <h1 className="font-display text-2xl font-bold tracking-tight">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome back. Enter your credentials.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-white/[0.1] bg-[#0A0A0A] focus-visible:ring-white/20"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-white/[0.1] bg-[#0A0A0A] focus-visible:ring-white/20"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground" data-testid="login-remember-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                data-testid="login-remember"
                className="h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full border border-white/30 bg-transparent transition-colors"
                style={remember ? { background: "#FFFFFF", borderColor: "#FFFFFF" } : undefined} />
              Trust this device for 30 days
            </label>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-white text-black hover:bg-white/90"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <button type="button" onClick={() => setForgot(true)} data-testid="login-forgot-link"
            className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
            Forgot password?
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
